import { Component, ComponentFactory, ComponentFactoryResolver, ComponentRef, Injector, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { StepperService } from '../../services/stepper.service';
import { FormGroup } from '@angular/forms';
import Swal from 'sweetalert2';
import { MatDialogRef } from '@angular/material/dialog';
import { getDynamicStepFlow, STEP_COMPONENTS, STEPS_CONFIG, STEPS_UPDATE_PERSONAL_INFORMATION } from './initial-form.constants';
import { StepComponent } from './initialForm.models';

@Component({
  selector: 'app-initial-form',
  templateUrl: './initial-form.component.html',
  styleUrls: ['./initial-form.component.scss']
})
export class InitialFormComponent implements OnInit, OnDestroy {
  stepperForm: FormGroup | null = null;
  currentStep = 0;
  isLoading = true;

  steps = STEPS_CONFIG;
  stepsUpdatePersonalInformation = STEPS_UPDATE_PERSONAL_INFORMATION;
  currentComponent: any = null;

  @ViewChild('stepContainer', { read: ViewContainerRef }) stepContainer!: ViewContainerRef;
  private currentComponentRef: ComponentRef<any> | null = null;

  constructor(
    private stepperService: StepperService,
    private dialogRef: MatDialogRef<InitialFormComponent>,
    private resolver: ComponentFactoryResolver
  ) { }

  ngOnInit(): void {
    this.currentStep = this.stepperService.getStep();
    this.stepperService.loadUserData().subscribe(() => {
      this.isLoading = false;
      this.initializeSteps();
      this.setupStudyInterestWatcher();

      setTimeout(() => this.updateCurrentComponent(), 0);
    });

    this.stepperForm = this.stepperService.getStepperForm();
  }

  ngOnDestroy(): void {
    if (this.currentComponentRef) {
      this.currentComponentRef.destroy();
    }
  }

  private initializeSteps() {
    this.stepperForm = this.stepperService.getStepperForm();

    const interest = this.stepperForm.get('studyPreferences.studyInterest')?.value;
    this.steps = getDynamicStepFlow(interest);
  }

  private setupStudyInterestWatcher(): void {
    this.stepperForm.get('studyPreferences.studyInterest')?.valueChanges.subscribe((value: string) => {
      const previousStepKey = this.steps[this.currentStep]?.key;
      this.steps = getDynamicStepFlow(value);

      // Reposition if current step disappeared
      const index = this.steps.findIndex(s => s.key === previousStepKey);
      this.currentStep = index !== -1 ? index : 0;

      this.stepperService.setCurrentStep(this.currentStep);
      this.updateCurrentComponent();
    });
  }

  nextStep() {
    if (this.currentStep < this.steps.length - 1) {
      const currentStepForm = this.getCurrentStepForm();
      if (currentStepForm) {
        currentStepForm.markAllAsTouched();
        if (currentStepForm.invalid) return;
      }
      this.saveProgress();
      this.currentStep++;
      this.stepperService.setCurrentStep(this.currentStep);
      this.updateCurrentComponent();
    }
  }

  prevStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.stepperService.setCurrentStep(this.currentStep);
      this.updateCurrentComponent();
    }
  }



  updateCurrentComponent() {
    const currentKey = this.steps[this.currentStep]?.key;
    const component = STEP_COMPONENTS[currentKey];
    if (!component || !this.stepContainer) return;

    this.stepContainer.clear();

    const factory = this.resolver.resolveComponentFactory<StepComponent>(component);
    const componentRef = this.stepContainer.createComponent(factory);

    const currentForm = this.getCurrentStepForm() ?? this.stepperForm;
    componentRef.instance.form = currentForm;

    this.currentComponentRef = componentRef;
  }

  saveProgress() {
    const currentStepKey = this.steps[this.currentStep].key;

    if (this.stepsUpdatePersonalInformation.includes(currentStepKey)) {
      this.stepperService.saveProgress();
      this.stepperService.savePersonalInformation();
      return;
    }

    this.stepperService.saveProgress();
  }

  isNextStepDisabled(): boolean {
    if (this.isLoading) return true;

    const currentStepForm = this.getCurrentStepForm();

    if (!currentStepForm) return false;

    if (currentStepForm.invalid && currentStepForm.touched) {
      return true;
    }

    return false;

  }

  getCurrentStepForm(): FormGroup | null {
    if (!this.stepperForm) return null;

    return this.stepperForm.get(this.steps[this.currentStep].key) as FormGroup;
  }

  publishForm() {
    const currentStepForm = this.getCurrentStepForm();

    if (currentStepForm) {
      currentStepForm.markAllAsTouched();

      if (currentStepForm.invalid) {
        return;
      }
    }

    this.stepperService.publishForm().subscribe(
      () => {
        Swal.fire({
          title: 'Éxito',
          text: 'Tu formulario ha sido publicado exitosamente',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          this.dialogRef.close();
        }, (error) => {
          Swal.fire({
            title: 'Error',
            text: 'Ha ocurrido un error al publicar tu formulario',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        });
      }
    );
  }
}
